import type { TFunction } from 'i18next';
import Swal from 'sweetalert2';

import type {
    AppendFileTransferRequestDetailsPayload,
    CreateFileTransferRequestPayload,
    CreateFileTransferRequestResponse,
    UpdateFileTransferRequestResponse,
} from '../../../types/StudioFileManager';
import { StudioFileManagerCommand } from '../../../services/studio/StudioCommand';
import { handleStudioApiResponse } from '../../helper/studio_helper';
import { UploadItem } from './grpcFileManagerTypes';
import {
    buildSyncedUpdateResult,
    buildUpdateFileTransferPayload,
} from './grpcFileManagerUploadHelpers';

function showStatusUpdateSuccessToast(t: TFunction): void {
    Swal.fire({
        icon: 'success',
        title: t('studio.upload.statusUpdateSuccess'),
        showConfirmButton: false,
        timer: 2000,
    });
}

function parseUpdateFileTransferResponse(res: unknown): UpdateFileTransferRequestResponse | null {
    const updateResponse = ((res as { data?: UpdateFileTransferRequestResponse })?.data ||
        res) as UpdateFileTransferRequestResponse;
    return updateResponse?.fileTransferRequestId ? updateResponse : null;
}

function parseCreateFileTransferResponse(res: unknown): CreateFileTransferRequestResponse | null {
    const response =
        (res as { data?: CreateFileTransferRequestResponse })?.data ??
        (res as CreateFileTransferRequestResponse);
    return response?.fileTransferRequestId ? response : null;
}

export type SendUpdateFileTransferDeps = {
    t: TFunction;
    setUpdateResult: (result: UpdateFileTransferRequestResponse | null) => void;
    setStatus: (status: string) => void;
    setIsSelectionLoading: (loading: boolean) => void;
};

/** Module-level to keep nesting ≤4 (S2004) vs defining inside the React component. */
export function executeSendUpdateFileTransferRequest(
    requestId: number,
    currentItems: UploadItem[],
    deps: SendUpdateFileTransferDeps
): Promise<void> {
    const updatePayload = buildUpdateFileTransferPayload(requestId, currentItems);
    const completedCount = updatePayload.fileDetails.filter((d) => d.isSuccess).length;
    const withPathCount = updatePayload.fileDetails.filter((d) => !!d.FileUploadPath?.trim()).length;

    console.info('[FileTransferTrace] Sending UpdateFileTransferRequest reconciliation', {
        requestId,
        detailCount: updatePayload.fileDetails.length,
        completedCount,
        withPathCount,
        details: updatePayload.fileDetails.map((d) => ({
            detailId: d.fileTransferRequestDetailId,
            isSuccess: d.isSuccess,
            hasPath: !!d.FileUploadPath?.trim(),
            path: d.FileUploadPath || '',
        })),
    });

    return new Promise<void>((resolve) => {
        void handleStudioApiResponse(
            StudioFileManagerCommand.UpdateFileTransferRequest(updatePayload),
            (res: unknown) => {
                const updateResponse = parseUpdateFileTransferResponse(res);
                if (updateResponse) {
                    console.info('[FileTransferTrace] UpdateFileTransferRequest succeeded', {
                        requestId,
                        successfulFiles: updateResponse.successfulFiles,
                        failedFiles: updateResponse.failedFiles,
                        isCompleted: updateResponse.isCompleted,
                    });
                    deps.setUpdateResult(updateResponse);
                    deps.setStatus('');
                    showStatusUpdateSuccessToast(deps.t);
                } else {
                    console.warn('[FileTransferTrace] UpdateFileTransferRequest returned empty response; using local synced result', {
                        requestId,
                    });
                    deps.setUpdateResult(buildSyncedUpdateResult(requestId, currentItems));
                    deps.setStatus('');
                    showStatusUpdateSuccessToast(deps.t);
                }
                resolve();
            },
            () => {
                console.error('[FileTransferTrace] UpdateFileTransferRequest request error callback', { requestId });
                deps.setIsSelectionLoading(false);
                resolve();
            },
            deps.t,
            () => {
                console.error('[FileTransferTrace] UpdateFileTransferRequest failed', { requestId });
                deps.setStatus(deps.t('studio.upload.statusUpdateFailed'));
            }
        );
    });
}

async function parseCreateOrAppendResponse(
    promise: Promise<any>,
    t: TFunction
): Promise<CreateFileTransferRequestResponse | null> {
    try {
        const res: { errors?: Record<string, string[]>; error?: string; data?: CreateFileTransferRequestResponse } &
            CreateFileTransferRequestResponse = await promise;
        if (res?.errors) {
            const errorMessages = Object.values(res.errors).flat().join('\n');
            Swal.fire(errorMessages, '', 'error');
            return null;
        }
        if (res?.error) {
            Swal.fire(res.error, '', 'error');
            return null;
        }
        const response = (res?.data || res) as CreateFileTransferRequestResponse;
        return response?.fileTransferRequestId ? response : null;
    } catch (error: unknown) {
        const errorData = (error as { data?: { errors?: Record<string, string[]> } })?.data?.errors;
        if (errorData) {
            const errorMessages = Object.values(errorData).flat().join('\n');
            Swal.fire(errorMessages, '', 'error');
        } else {
            Swal.fire(t('general.anErrorOccured'), '', 'error');
        }
        return null;
    }
}

export async function executeCreateFileTransferRequestOnce(
    payload: CreateFileTransferRequestPayload,
    t: TFunction
): Promise<CreateFileTransferRequestResponse | null> {
    return parseCreateOrAppendResponse(StudioFileManagerCommand.CreateFileTransferRequest(payload), t);
}

export async function executeAppendFileTransferRequestDetailsOnce(
    payload: AppendFileTransferRequestDetailsPayload,
    t: TFunction
): Promise<CreateFileTransferRequestResponse | null> {
    return parseCreateOrAppendResponse(StudioFileManagerCommand.AppendFileTransferRequestDetails(payload), t);
}

export function executeCreateFileTransferRequest(
    payload: CreateFileTransferRequestPayload,
    deps: Pick<SendUpdateFileTransferDeps, 't' | 'setIsSelectionLoading'>
): Promise<CreateFileTransferRequestResponse | null> {
    return new Promise((resolve) => {
        handleStudioApiResponse(
            StudioFileManagerCommand.CreateFileTransferRequest(payload),
            (res: unknown) => {
                resolve(parseCreateFileTransferResponse(res));
            },
            () => {
                deps.setIsSelectionLoading(false);
                resolve(null);
            },
            deps.t
        );
    });
}
